/**
 * TimeRequestNotification Component
 * Displays a notification when someone requests a custom time
 * for a coaching session or ritual booking.
 */

import { Calendar, Clock } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { formatNotificationTime } from "@/utils/formatTime"
import type { Notification } from "../services/notification.service"

interface TimeRequestNotificationProps {
  notification: Notification
  onRead?: () => void
  onUnread?: () => void
  onNotificationClick?: (notification: Notification) => void
}

interface TimeRequestData {
  coachingSessionId?: string
  ritualId?: string
  requesterId?: string
  requesterName?: string
  requesterUsername?: string | null
  preferredDates?: string[]
  preferredTimes?: string[]
  timezone?: string
  message?: string | null
}

function getUserDisplayImage(user: Notification["triggeredByUser"]): string | undefined {
  if (!user) return undefined
  if (user.profile_photos && user.profile_photos.length > 0) {
    const coverPhoto = user.profile_photos.find(p => p.isCover || p.is_cover_image)
    if (coverPhoto?.filePath) {
      return coverPhoto.filePath
    }
  }
  return user.profilePictureUrl ?? undefined
}

function formatDateDisplay(dateStr: string): string {
  try {
    const date = new Date(dateStr)
    if (Number.isNaN(date.getTime())) return dateStr
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    })
  } catch {
    return dateStr
  }
}

function formatTimeDisplay(timeStr: string): string {
  try {
    const [hours, minutes] = timeStr.split(":").map(Number)
    const period = hours >= 12 ? "PM" : "AM"
    const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours
    const endHours = (hours + 1) % 24
    const endPeriod = endHours >= 12 ? "PM" : "AM"
    const displayEndHours = endHours > 12 ? endHours - 12 : endHours === 0 ? 12 : endHours
    return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period} - ${displayEndHours}:${minutes.toString().padStart(2, "0")} ${endPeriod}`
  } catch {
    return timeStr
  }
}

export function TimeRequestNotification({
  notification,
  onRead,
  onUnread,
  onNotificationClick,
}: TimeRequestNotificationProps) {
  const data = (notification.data as TimeRequestData) ?? {}
  const isRead = notification.isRead

  const requester = notification.triggeredByUser
  const displayImageUrl = getUserDisplayImage(requester)
  const displayName = requester?.name || requester?.username || data.requesterName || "Someone"
  const displayUsername = requester?.username ?? data.requesterUsername ?? undefined

  const relativeTime = formatNotificationTime(notification.createdAt)

  const hasDates = (data.preferredDates?.length ?? 0) > 0
  const hasTimes = (data.preferredTimes?.length ?? 0) > 0
  const hasMessage = Boolean(data.message?.trim())

  return (
    <div
      className={`flex flex-col gap-3 p-4 rounded-md transition-colors border cursor-pointer ${
        !isRead ? "notification-unread" : "notification-read"
      }`}
      onClick={() => {
        onRead?.()
        onNotificationClick?.(notification)
      }}
    >
      {/* Header Row */}
      <div className="relative flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          {/* Requester avatar */}
          <div className="w-12 h-12 rounded-full overflow-hidden bg-card flex-shrink-0">
            {displayImageUrl ? (
              <img src={displayImageUrl} alt={displayName} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-primary bg-primary/10 text-lg">
                {displayName[0]}
              </div>
            )}
          </div>

          {/* Name, Username, Time */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-primary text-[14px]">{displayName}</span>
              {displayUsername && (
                <span className="text-stone text-[14px]">@{displayUsername}</span>
              )}
              <span className="text-stone/50 text-[14px]">{relativeTime}</span>
            </div>

            {/* Main message */}
            <div className="text-[14px] text-primary leading-[25px]">{notification.message}</div>

            {/* Requested date */}
            {hasDates && (
              <div className="flex items-center gap-1.5 mt-1 text-[13px] text-stone">
                <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                <span>{data.preferredDates?.map(d => formatDateDisplay(d)).join(", ")}</span>
              </div>
            )}

            {/* Requested time */}
            {hasTimes && (
              <div className="flex items-center gap-1.5 text-[13px] text-stone">
                <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                <span>{data.preferredTimes?.map(t => formatTimeDisplay(t)).join(", ")}</span>
              </div>
            )}

            {/* User message */}
            {hasMessage && (
              <div className="flex items-start gap-1.5 mt-1.5 text-[13px] text-stone">
                <span className="italic">"{data.message}"</span>
              </div>
            )}
          </div>
        </div>

        {/* Menu */}
        <div className="absolute -top-2 -right-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="w-8 h-8 rounded-full flex items-center justify-center transition-colors cursor-pointer"
                onClick={e => e.stopPropagation()}
              >
                <svg className="w-4 h-4 text-stone" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="12" cy="5" r="1.5" />
                  <circle cx="12" cy="12" r="1.5" />
                  <circle cx="12" cy="19" r="1.5" />
                </svg>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              {isRead ? (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    onUnread?.()
                  }}
                  className="text-[11px] cursor-pointer"
                >
                  Mark as unread
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    onRead?.()
                  }}
                  className="text-[11px] cursor-pointer"
                >
                  Mark as read
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  )
}
